import { Product, CartItem, SelectedVariation } from '@/types';

export interface DiscountInfo {
  hasDiscount: boolean;
  discountType: 'fixed' | 'percentage';
  discountAmount: number;
  threshold: number;
  currentQuantity: number;
  isEligible: boolean;
  discountValue: number;
  originalPrice: number;
  finalPrice: number;
}

export function calculateProductDiscount(product: Product, quantity: number = 1): DiscountInfo {
  const sellingPrice = parseFloat(product.sellingprice);

  // 1. First, check for General (Product-Level) Quantity-Based Pricing
  // This has highest priority and overrides variation QBP
  if (product.hasQuantityPricing && product.qty_based_pricing) {
    let tiers: Array<{min: number, max: number, price: number}> = [];
    
    try {
      const parsed = typeof product.qty_based_pricing === 'string' 
        ? JSON.parse(product.qty_based_pricing) 
        : product.qty_based_pricing;
      
      if (Array.isArray(parsed)) {
        tiers = parsed
          .map((t: any) => ({
            min: Number(t.minQuantity || 0),
            max: Number(t.maxQuantity || 0),
            price: Number(t.price || 0)
          }))
          .filter(t => t.price > 0)
          .sort((a, b) => a.min - b.min);
      }
    } catch (e) {
      console.error('Error parsing product qty_based_pricing:', e);
    }

    if (tiers.length > 0) {
      const matchingTier = tiers.find((tier) => quantity >= tier.min && quantity <= tier.max);
      let effectivePrice = sellingPrice;

      if (matchingTier) {
        effectivePrice = matchingTier.price;
      } else {
        // Check if quantity exceeds max tier - use the highest tier price
        const maxTier = tiers[tiers.length - 1];
        if (quantity >= maxTier.min) {
          effectivePrice = maxTier.price;
        }
      }

      return {
        hasDiscount: effectivePrice !== sellingPrice,
        discountType: 'fixed',
        discountAmount: sellingPrice - effectivePrice,
        threshold: tiers[0]?.min || 0,
        currentQuantity: quantity,
        isEligible: effectivePrice < sellingPrice,
        discountValue: sellingPrice - effectivePrice,
        originalPrice: sellingPrice,
        finalPrice: effectivePrice
      };
    }
  }

  // 2. Check for Variation-Level Quantity-Based Pricing (e.g., "Nil" variation)
  const nilVariation = product.variations?.find(v => v.variation.toLowerCase() === 'nil');
  if (nilVariation?.hasQuantityPricing && nilVariation?.quantityPricing?.length) {
    const tiers = nilVariation.quantityPricing
      .map(t => ({
        min: Number(t.minQuantity || 0),
        max: Number(t.maxQuantity || 0),
        price: Number(t.price || 0)
      }))
      .filter(t => t.price > 0)
      .sort((a, b) => a.min - b.min);

    const matchingTier = tiers.find((tier) => quantity >= tier.min && quantity <= tier.max);
    let effectivePrice = sellingPrice;

    if (matchingTier) {
      effectivePrice = matchingTier.price;
    } else if (tiers.length > 0) {
      const maxTier = tiers[tiers.length - 1];
      if (quantity >= maxTier.max) {
        effectivePrice = maxTier.price;
      }
    }

    return {
      hasDiscount: effectivePrice !== sellingPrice,
      discountType: 'fixed',
      discountAmount: sellingPrice - effectivePrice,
      threshold: tiers[0]?.min || 0,
      currentQuantity: quantity,
      isEligible: effectivePrice < sellingPrice,
      discountValue: sellingPrice - effectivePrice,
      originalPrice: sellingPrice,
      finalPrice: effectivePrice
    };
  }

  // Standard product discount logic (existing code)
  const discountOff = parseFloat(product.discount_off) || 0;
  const discountQty = parseInt(product.discount_qty) || 0;

  const hasDiscount = discountOff > 0 && discountQty > 0;

  if (!hasDiscount) {
    return {
      hasDiscount: false,
      discountType: 'fixed',
      discountAmount: 0,
      threshold: 0,
      currentQuantity: quantity,
      isEligible: false,
      discountValue: 0,
      originalPrice: sellingPrice,
      finalPrice: sellingPrice
    };
  }

  const isEligible = quantity >= discountQty;
  let discountValue = 0;
  let finalPrice = sellingPrice;

  if (isEligible) {
    if (product.discount_type === 'percentage') {
      discountValue = (sellingPrice * discountOff) / 100;
      finalPrice = sellingPrice - discountValue;
    } else {
      discountValue = discountOff;
      finalPrice = Math.max(0, sellingPrice - discountOff);
    }
  }

  return {
    hasDiscount: true,
    discountType: product.discount_type as 'fixed' | 'percentage',
    discountAmount: discountOff,
    threshold: discountQty,
    currentQuantity: quantity,
    isEligible,
    discountValue,
    originalPrice: sellingPrice,
    finalPrice
  };
}

export function calculateVariationDiscount(
  product: Product, 
  variation: SelectedVariation, 
  allVariations: SelectedVariation[]
): DiscountInfo {
  const variationData = product.variations.find(v => v.variation === variation.variation);

  let variationPrice = variationData ? parseFloat(variationData.variation_amount) : 0;
  
  // Calculate total quantity across all variations for this product
  const totalQuantity = allVariations.reduce((sum, v) => sum + v.quantity, 0);

  // 1. First check for General (Product-Level) Quantity-Based Pricing
  // This overrides variation-level QBP
  if (product.hasQuantityPricing && product.qty_based_pricing) {
    let tiers: Array<{min: number, max: number, price: number}> = [];
    
    try {
      const parsed = typeof product.qty_based_pricing === 'string' 
        ? JSON.parse(product.qty_based_pricing) 
        : product.qty_based_pricing;
      
      if (Array.isArray(parsed)) {
        tiers = parsed
          .map((t: any) => ({
            min: Number(t.minQuantity || 0),
            max: Number(t.maxQuantity || 0),
            price: Number(t.price || 0)
          }))
          .filter(t => t.price > 0)
          .sort((a, b) => a.min - b.min);
      }
    } catch (e) {
      console.error('Error parsing product qty_based_pricing:', e);
    }

    if (tiers.length > 0) {
      const matchingTier = tiers.find((tier) => totalQuantity >= tier.min && totalQuantity <= tier.max);
      if (matchingTier) {
        variationPrice = matchingTier.price;
      } else {
        // Check if total quantity exceeds max tier
        const maxTier = tiers[tiers.length - 1];
        if (totalQuantity >= maxTier.min) {
          variationPrice = maxTier.price;
        }
      }
      
      // Return early with General QBP pricing
      const discountValue = parseFloat(variationData?.variation_amount || product.sellingprice) - variationPrice;
      return {
        hasDiscount: discountValue > 0,
        discountType: 'fixed',
        discountAmount: discountValue,
        threshold: tiers[0]?.min || 0,
        currentQuantity: totalQuantity,
        isEligible: discountValue > 0,
        discountValue: discountValue,
        originalPrice: parseFloat(variationData?.variation_amount || product.sellingprice),
        finalPrice: variationPrice
      };
    }
  }

  // 2. Apply quantity-based pricing if present on variation (only if no General QBP)
  // Check both quantityPricing array and qty_based_pricing JSON string
  let variationTiers: Array<{min: number, max: number, price: number}> = [];
  
  // Check for hasQuantityPricing (camelCase) or has_quantity_pricing (snake_case from API)
  const hasVarQuantityPricing = variationData?.hasQuantityPricing || (variationData as any)?.has_quantity_pricing;
  
  if (hasVarQuantityPricing) {
    if (variationData?.quantityPricing?.length) {
      // Use pre-parsed array if available
      variationTiers = variationData.quantityPricing
        .map(t => ({
          min: Number(t.minQuantity || 0),
          max: Number(t.maxQuantity || 0),
          price: Number(t.price || 0)
        }))
        .filter(t => t.price > 0);
    } else if (variationData?.qty_based_pricing || (variationData as any)?.qty_based_pricing) {
      // Parse JSON string from API
      try {
        const rawPricing = variationData?.qty_based_pricing || (variationData as any)?.qty_based_pricing;
        const parsed = typeof rawPricing === 'string'
          ? JSON.parse(rawPricing)
          : rawPricing;
        
        if (Array.isArray(parsed)) {
          variationTiers = parsed
            .map((t: any) => ({
              min: Number(t.minQuantity || 0),
              max: Number(t.maxQuantity || 0),
              price: Number(t.price || 0)
            }))
            .filter(t => t.price > 0);
        }
      } catch (e) {
        console.error('Error parsing variation qty_based_pricing:', e);
      }
    }
  }
  
  if (variationTiers.length > 0) {
    // Sort tiers by min quantity
    variationTiers.sort((a, b) => a.min - b.min);
    
    const qty = variation.quantity;
    const originalVariationPrice = variationPrice;
    const minTier = variationTiers[0];
    
    // Check if quantity qualifies for any tier
    const matchingTier = variationTiers.find((tier) => qty >= tier.min && qty <= tier.max);
    
    let tierMatched = false;
    if (matchingTier) {
      variationPrice = matchingTier.price;
      tierMatched = true;
    } else if (qty >= minTier?.min) {
      // If qty exceeds max tier, use highest tier price
      const maxTier = variationTiers[variationTiers.length - 1];
      if (qty >= maxTier.min) {
        variationPrice = maxTier.price;
        tierMatched = true;
      }
    }
    
    // Only apply discount if a tier was actually matched
    if (tierMatched) {
      const varDiscountValue = originalVariationPrice - variationPrice;
      
      // Only return discount if price is actually lower
      if (varDiscountValue > 0) {
        return {
          hasDiscount: true,
          discountType: 'fixed',
          discountAmount: varDiscountValue,
          threshold: minTier?.min || 0,
          currentQuantity: qty,
          isEligible: true,
          discountValue: varDiscountValue,
          originalPrice: originalVariationPrice,
          finalPrice: variationPrice
        };
      }
    }
  }
  
  const discountOff = parseFloat(product.discount_off) || 0;
  const discountQty = parseInt(product.discount_qty) || 0;
  
  const hasStandardDiscount = discountOff > 0 && discountQty > 0;
  
  if (!hasStandardDiscount) {
    return {
      hasDiscount: false,
      discountType: 'fixed',
      discountAmount: 0,
      threshold: 0,
      currentQuantity: totalQuantity,
      isEligible: false,
      discountValue: 0,
      originalPrice: variationPrice,
      finalPrice: variationPrice
    };
  }

  const isEligible = totalQuantity >= discountQty;
  let standardDiscountValue = 0;
  let finalPrice = variationPrice;

  if (isEligible) {
    if (product.discount_type === 'percentage') {
      standardDiscountValue = (variationPrice * discountOff) / 100;
      finalPrice = variationPrice - standardDiscountValue;
    } else {
      standardDiscountValue = discountOff;
      finalPrice = Math.max(0, variationPrice - discountOff);
    }
  }

  return {
    hasDiscount: true,
    discountType: product.discount_type as 'fixed' | 'percentage',
    discountAmount: discountOff,
    threshold: discountQty,
    currentQuantity: totalQuantity,
    isEligible,
    discountValue: standardDiscountValue,
    originalPrice: variationPrice,
    finalPrice
  };
}

export function calculateCartItemDiscount(cartItem: CartItem): {
  totalOriginalPrice: number;
  totalDiscountValue: number;
  totalFinalPrice: number;
  discountInfo: DiscountInfo;
} {
  if (cartItem.selectedVariations && cartItem.selectedVariations.length > 0) {
    let totalOriginalPrice = 0;
    let totalDiscountValue = 0;
    let totalFinalPrice = 0;

    // Calculate discount for each variation
    cartItem.selectedVariations.forEach(variation => {
      const discountInfo = calculateVariationDiscount(cartItem, variation, cartItem.selectedVariations!);
      const variationTotal = discountInfo.originalPrice * variation.quantity;
      const variationDiscountTotal = discountInfo.discountValue * variation.quantity;
      const variationFinalTotal = discountInfo.finalPrice * variation.quantity;

      totalOriginalPrice += variationTotal;
      totalDiscountValue += variationDiscountTotal;
      totalFinalPrice += variationFinalTotal;
    });

    // Get discount info from first variation (they all share the same product discount rules)
    const firstVariation = cartItem.selectedVariations[0];
    const discountInfo = calculateVariationDiscount(cartItem, firstVariation, cartItem.selectedVariations);

    return {
      totalOriginalPrice,
      totalDiscountValue,
      totalFinalPrice,
      discountInfo
    };
  } else {
    const discountInfo = calculateProductDiscount(cartItem, cartItem.quantity);
    const totalOriginalPrice = discountInfo.originalPrice * cartItem.quantity;
    const totalDiscountValue = discountInfo.discountValue * cartItem.quantity;
    const totalFinalPrice = discountInfo.finalPrice * cartItem.quantity;

    return {
      totalOriginalPrice,
      totalDiscountValue,
      totalFinalPrice,
      discountInfo
    };
  }
}

export function getEffectiveVariationPrice(product: Product, variation: SelectedVariation, allVariations: SelectedVariation[]): number {
  const discountInfo = calculateVariationDiscount(product, variation, allVariations);
  return discountInfo.finalPrice;
}

export function getEffectiveProductPrice(product: Product, quantity: number): number {
  const discountInfo = calculateProductDiscount(product, quantity);
  return discountInfo.finalPrice;
}

export function formatDiscountDisplay(discountType: 'fixed' | 'percentage', discountAmount: number): string {
  if (discountType === 'percentage') {
    return `${discountAmount}% OFF`;
  } else {
    // Always use NGN (₦) for discount display to match store currency
    return `₦${discountAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} OFF`;
  }
}

export function getVariationPricingType(variation: SelectedVariation): 'quantity-tier' | 'variation' {
  if (variation.hasQuantityPricing && Array.isArray(variation.quantityPricing)) {
    const qty = Number(variation.quantity || 0);
    const match = variation.quantityPricing.find((tier) => {
      const min = Number(tier.minQuantity || 0);
      const max = Number(tier.maxQuantity || 0);
      return qty >= min && qty <= max;
    });
    if (match && Number(match.price) > 0) return 'quantity-tier';
  }
  return 'variation';
}

export function getProductPricingType(product: Product, quantity: number): 'product-discount' | 'standard' {
  const basePrice = Number(product.sellingprice || '0');
  const effectivePrice = getEffectiveProductPrice(product, quantity);
  if (effectivePrice !== basePrice) {
    return 'product-discount';
  }
  return 'standard';
}